import type {TrackingServiceConfig} from '@shared/types/TrackingService'
import type {Location} from 'react-router-dom'
import {getFailSafeLocalStorage} from 'swayok-react-mdb-ui-kit/helpers/local_storage/getFailSafeLocalStorage'
import {DateTimeService} from 'swayok-react-mdb-ui-kit/services/DateTimeService'
import {NavigationService} from 'swayok-react-mdb-ui-kit/services/NavigationService'
import type {AnyObject} from 'swayok-react-mdb-ui-kit/types/Common'

// Настройки по-умолчанию.
const defaultConfig: TrackingServiceConfig = {
    apiUrl: '/crm-api',
    siteId: -1,
    trackingId: null,
    trackingIdKey: 't',
    trackingIdCommitedKey: 'tCommited',
    trackingIdLength: 64,
    referrerKey: 'referrer',
    visitAtKey: 'visitAt',
    lastActionAtKey: 'lastActionAt',
    sessionLifetime: 3600, // 60 минут в секундах.
    shortSessionLength: 60, // 60 секунд.
    sessionUpdateInterval: 60000, // 60 секунд в миллисекундах.
    shortSessionUpdateInterval: 30000, // 30 секунд в миллисекундах.
}

// Тип ответа от API.
interface ApiResponseData {
    data?: {
        created_at?: string
    }
}

// Сервис трекинга действий пользователя.
export class TrackingService {

    // Зарегистрированы ли обработчики событий?
    private static isEventHandlersRegistered: boolean = false
    // Настройки.
    private static config: TrackingServiceConfig = {...defaultConfig}
    private static locale: string = 'vi-VN'
    private static authToken: string | null = null
    // Последний известный URL.
    private static lastUrl: string | null = null
    // Форматирование даты и времени.
    private static dateTimeFormat: string = 'YYYY-MM-DD HH:mm:ss'

    // Инициализация трекинга.
    static init(
        config: Partial<TrackingServiceConfig>,
        locale: string,
        authToken: string | null = null
    ): void {
        this.config = {...defaultConfig, ...config}
        this.locale = locale
        this.authToken = authToken

        // Генерим trackingId.
        if (!this.getTrackingId()) {
            this.getLocalStorage().setItem(
                this.config.trackingIdKey,
                this.generateRandomString(this.config.trackingIdLength)
            )
            this.getLocalStorage().setItem(this.config.trackingIdCommitedKey, '0')
        }

        // Сохраняем самый первый referrer.
        if (!document.referrer.includes(window.location.hostname)) {
            this.getLocalStorage().setItem(this.config.referrerKey, document.referrer)
        }

        // Время начала визита.
        if (!this.getLocalStorage().getItem(this.config.visitAtKey) || !this.isSessionActive()) {
            this.getLocalStorage().setItem(
                this.config.visitAtKey,
                DateTimeService.utc().format(this.dateTimeFormat)
            )
        }

        // Регистрация обработчиков событий взаимодействия с сайтом.
        if (!this.isEventHandlersRegistered) {
            this.registerActionEvents()
            this.isEventHandlersRegistered = true
        }
    }

    // Задать новую локаль.
    static setLocale(locale: string): void {
        this.locale = locale
    }

    // Задание токена авторизации.
    static setAuthToken(token: string | null): void {
        this.authToken = token
    }

    // Получение trackingId.
    static getTrackingId(): string | null {
        return this.config.trackingId ?? this.getLocalStorage().getItem(this.config.trackingIdKey)
    }

    // Получить самый первый referrer.
    static getReferrer(): string | null {
        return this.getLocalStorage().getItem(this.config.referrerKey)
    }

    // Обработчики событий взаимодействия с сайтом.
    private static registerActionEvents(): void {

        document.addEventListener('click', () => {
            this.updateLastAction()
        })

        window.addEventListener('scroll', () => {
            this.updateLastAction()
        })

        window.addEventListener('pagehide', () => {
            this.updateLastAction()
            this.updateSession(true, false)
        }, false)

        // Отслеживание перемещения между страницами.
        NavigationService.registerNavigationCallback(
            'TrackingService',
            (location: Location) => {
                this.trackUrlChange(
                    NavigationService.makeAbsoluteUrlFormLocation(location)
                )
            }
        )

        // Если сессия только началась, то запросы на обновление отправляем чаще.
        const timeoutUpdateSession = () => {
            this.updateSession(false, true)
            setTimeout(
                timeoutUpdateSession,
                this.isSessionShort()
                    ? this.config.shortSessionUpdateInterval
                    : this.config.sessionUpdateInterval
            )
        }
        // Стартуем поллинг.
        setTimeout(
            timeoutUpdateSession,
            this.isSessionShort()
                ? this.config.shortSessionUpdateInterval
                : this.config.sessionUpdateInterval
        )

        // Не нужно делать первый запрос явно - он будет сделан через
        // NavigationService.registerNavigationCallback (выше).
    }

    // Отслеживание перемещения между страницами.
    static trackUrlChange(absoluteUrl: string): void {
        if (this.lastUrl !== absoluteUrl) {
            this.lastUrl = absoluteUrl
            this.updateLastAction()
            this.updateSession(false, true)
            this.reportUrlChange(absoluteUrl)
        }
    }

    // Отправить данные поведения пользователя на страницах ввода данных клиента CRM.
    static trackBehavior(behaviorJson: string | null): void {
        if (behaviorJson) {
            const apiUrl: string = this.config.apiUrl + '/v2/interactions'
            const data: string = JSON.stringify({
                tracking_id: this.getTrackingId(),
                behavior: behaviorJson,
            })
            const xhr: XMLHttpRequest = this.createXhrRequest(apiUrl, 'POST', true)
            xhr.send(data)
        }
    }

    // Уведомить об изменении URL.
    private static reportUrlChange(absoluteUrl: string): void {
        const apiUrl: string = this.config.apiUrl + '/v2/nav'
        const data: string = JSON.stringify({
            url: absoluteUrl,
            tracking_id: this.getTrackingId(),
            screen_resolution: {
                width: window.screen.width,
                height: window.screen.height,
            },
        })
        const xhr: XMLHttpRequest = this.createXhrRequest(apiUrl, 'POST', true)
        xhr.send(data)
    }

    // Обновление последнего действия на сайте.
    private static updateLastAction(): void {
        this.getLocalStorage().setItem(
            this.config.lastActionAtKey,
            DateTimeService.utc().format(this.dateTimeFormat)
        )
    }

    // Проверка активности сессии.
    private static isSessionActive(): boolean {
        const lastActionSecondsAgo = DateTimeService.utc().diff(
            DateTimeService.utc(this.getLocalStorage().getItem(this.config.lastActionAtKey)),
            'seconds'
        )
        return Math.abs(lastActionSecondsAgo) < this.config.sessionLifetime
    }

    // Признак того, что сессия является короткой.
    private static isSessionShort(): boolean {
        const sessionLength = DateTimeService.utc().diff(
            DateTimeService.utc(this.getLocalStorage().getItem(this.config.visitAtKey)),
            'seconds'
        )
        return sessionLength < this.config.shortSessionLength
    }

    // Обновление данных по текущей сессии.
    private static updateSession(tryUseBeacon: boolean, asyncXhr: boolean): void {
        if (this.isSessionActive()) {
            const apiUrl: string = this.config.apiUrl + '/v2/session'
            const data: string = JSON.stringify({
                tracking_id: this.getTrackingId(),
                screen_resolution: {
                    width: window.screen.width,
                    height: window.screen.height,
                },
            })
            if (tryUseBeacon && navigator.sendBeacon) {
                navigator.sendBeacon(apiUrl, data)
            } else {
                const xhr: XMLHttpRequest = this.createXhrRequest(apiUrl, 'POST', asyncXhr)
                xhr.onreadystatechange = () => {
                    if (xhr.readyState === 4) {
                        // Получен ответ от API.
                        try {
                            const response: ApiResponseData = JSON.parse(xhr.responseText)
                            if (response?.data?.created_at) {
                                this.getLocalStorage().setItem(
                                    this.config.visitAtKey,
                                    DateTimeService.utc(response.data.created_at)
                                        .format(this.dateTimeFormat)
                                )
                            }
                        } catch (_e) {
                            // Игнорируем.
                        }
                    }
                }
                xhr.send(data)
            }
        }
    }

    // Генерация случайной строки.
    private static generateRandomString(length: number): string {
        let result: string = ''
        const characters: string = 'ABCDEFGHIJKLMNOPQRSTUVWXYZabcdefghijklmnopqrstuvwxyz0123456789'
        const charactersLength: number = characters.length
        for (let i: number = 0; i < length; i++) {
            result += characters.charAt(Math.floor(Math.random() * charactersLength))
        }
        return result
    }

    // Создать и настроить XHR запрос, но не отправлять.
    private static createXhrRequest(
        url: string,
        method: 'GET' | 'POST',
        async: boolean = true
    ): XMLHttpRequest {
        const xhr: XMLHttpRequest = new XMLHttpRequest()
        // xhr.withCredentials = true
        const headers: AnyObject<string> = {
            Accept: 'application/json',
            'Content-Type': 'application/json; charset=utf-8',
            'Site-Id': String(this.config.siteId),
            'Accept-Language': this.locale,
            'Site-Locale': this.locale,
        }
        if (this.authToken) {
            headers.Authorization = 'Bearer ' + this.authToken
        }
        xhr.open(method, url, async)
        for (const headerName in headers) {
            xhr.setRequestHeader(headerName, headers[headerName])
        }
        return xhr
    }

    // Получить объект LocalStorage.
    // Стоит учитывать, что доступ к LocalStorage может быть ограничен и
    // будет вызывать ошибки вида:
    // Failed to read the 'localStorage' property from 'Window': Access is denied for this document.
    // Для этого требуется либо использовать DummyStorage (ничего не хранить),
    // либо FallbackStorage (хранить в массиве до перезагрузки страницы).
    private static getLocalStorage(): Storage {
        return getFailSafeLocalStorage()
    }
}
